// src/pages/public/AboutUs.jsx
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useBranding } from "../../hooks/useBranding";
import { useData } from "../../hooks/useData";
import { withCloudinaryLogoOptimization } from "../../utils/cloudinary";
import { NilamLogo, B, fonts, Btn } from "../../components/UI";
import SiteFooter from "../../components/SiteFooter";

export function AboutUs() {
  const { branding } = useBranding();
  const { categories } = useData();
  const accent = branding.primaryColor || B.orange;

  return (
    <>
      <Helmet>
        <title>About Us | {branding.brandName}</title>
        <meta name="description" content={`Learn about ${branding.brandName} — premium ladies kurti manufacturer based in Ahmedabad, Gujarat.`} />
      </Helmet>

      {/* Hero */}
      <div style={{ background: `linear-gradient(135deg, ${B.headerBg}, #3D1A00)`, padding: "60px 24px", textAlign: "center" }}>
        <NilamLogo size={64} logoUrl={withCloudinaryLogoOptimization(branding.logoUrl)} />
        <h1 style={{ fontFamily: fonts.display, fontSize: 40, color: B.white, margin: "16px 0 8px" }}>
          About {branding.brandName}
        </h1>
        <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 16, fontFamily: fonts.body, maxWidth: 500, margin: "0 auto" }}>
          {branding.tagline}
        </p>
      </div>

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "60px 24px" }}>

        {/* Story */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, marginBottom: 60, alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: accent, marginBottom: 12, fontFamily: fonts.body }}>Our Story</div>
            <h2 style={{ fontFamily: fonts.display, fontSize: 28, color: B.text, marginBottom: 18, lineHeight: 1.3 }}>Crafted with Passion, Worn with Pride</h2>
            <p style={{ fontFamily: fonts.body, fontSize: 14, color: B.textMid, lineHeight: 1.8, marginBottom: 14 }}>
              Nilam Synthetics is a leading ladies kurti manufacturer based in Ahmedabad, Gujarat — the fashion capital of India. We bring together traditional craftsmanship and modern design sensibilities to create kurtis that women love.
            </p>
            <p style={{ fontFamily: fonts.body, fontSize: 14, color: B.textMid, lineHeight: 1.8 }}>
              From 2-piece sets to 3-piece ensembles, from casual everyday wear to festival-ready designs, every piece is crafted with attention to detail, quality fabric, and vibrant colors that reflect the spirit of Indian fashion.
            </p>
          </div>
          <div style={{ background: `linear-gradient(135deg, ${B.orangeFaint}, ${B.orangeMid})`, borderRadius: 20, padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 52 }}>🧵</div>
            <div style={{ fontFamily: fonts.display, fontSize: 32, fontWeight: 700, color: accent, marginTop: 12 }}>2025</div>
            <div style={{ fontFamily: fonts.body, fontSize: 13, color: B.textMid, marginTop: 4 }}>Season Collection</div>
          </div>
        </div>

        {/* Values */}
        <div style={{ marginBottom: 60 }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 26, color: B.text, marginBottom: 28, textAlign: "center" }}>Why Choose Us</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20 }}>
            {[
              ["🌟", "Premium Quality",    "Only the finest fabrics — pure cotton, rayon, cambric — sourced carefully for comfort and durability."],
              ["🎨", "Latest Designs",     "Our in-house design team creates fresh collections every season, keeping you ahead of fashion trends."],
              ["💰", "Wholesale Pricing",  "Manufacturer-direct pricing means you get premium quality at the most competitive rates."],
              ["🚀", "Quick Dispatch",     "Orders processed and dispatched within 24-48 hours from our Ahmedabad facility."],
            ].map(([icon, title, desc]) => (
              <div key={title} style={{ background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 14, padding: "24px 20px", textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>{icon}</div>
                <div style={{ fontFamily: fonts.display, fontSize: 15, fontWeight: 700, color: B.text, marginBottom: 8 }}>{title}</div>
                <p style={{ fontFamily: fonts.body, fontSize: 12, color: B.textMid, lineHeight: 1.6 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div style={{ background: `linear-gradient(135deg, ${B.headerBg}, #3D1A00)`, borderRadius: 20, padding: "40px", textAlign: "center" }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 24, color: B.white, marginBottom: 8 }}>Get in Touch</h2>
          <p style={{ fontFamily: fonts.body, color: "rgba(255,255,255,0.6)", marginBottom: 24 }}>{branding.address}</p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            {branding.whatsapp && (
              <a href={`https://wa.me/${branding.whatsapp.replace(/\D/g,"")}?text=Hi! I want to know more about Nilam Synthetics.`}
                target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#25D366", color: "#fff", padding: "11px 22px", borderRadius: 10, fontWeight: 600, fontSize: 14, textDecoration: "none", fontFamily: fonts.body }}>
                💬 WhatsApp
              </a>
            )}
            {branding.phone && (
              <a href={`tel:${branding.phone}`}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, background: accent, color: "#fff", padding: "11px 22px", borderRadius: 10, fontWeight: 600, fontSize: 14, textDecoration: "none", fontFamily: fonts.body }}>
                📞 {branding.phone}
              </a>
            )}
          </div>
        </div>
      </div>

      <SiteFooter branding={branding} categories={categories} />
    </>
  );
}

// ─── How To Order ─────────────────────────────────────────────────────────────
export function HowToOrder() {
  const { branding } = useBranding();
  const { categories } = useData();
  const navigate     = useNavigate();
  const accent       = branding.primaryColor || B.orange;

  const steps = [
    { icon: "👀", title: "Browse the Catalog",   desc: "Explore our categories — 2Pcs, 3Pcs, Kurti, Coord Sets and more. Use search or filters to find exactly what you want." },
    { icon: "💬", title: "Enquire on WhatsApp",  desc: "Click the WhatsApp button on any design to quickly ask for details and availability." },
    { icon: "🤝", title: "Confirm Your Order",   desc: "Our team will share availability, stock colours, and pricing. We accept orders for individual pieces and bulk wholesale." },
    { icon: "💳", title: "Make Payment",         desc: "Payment via UPI, bank transfer, or cash on pickup. We'll share our payment details on WhatsApp." },
    { icon: "📦", title: "Dispatch & Delivery",  desc: "Orders are packed and dispatched within 24-48 hours. We ship pan-India via trusted courier partners." },
  ];

  return (
    <>
      <Helmet>
        <title>How To Order | {branding.brandName}</title>
        <meta name="description" content="Learn how to order from Nilam Synthetics — simple 5-step process via WhatsApp." />
      </Helmet>

      <div style={{ background: `linear-gradient(135deg, ${B.headerBg}, #3D1A00)`, padding: "60px 24px", textAlign: "center" }}>
        <h1 style={{ fontFamily: fonts.display, fontSize: 40, color: B.white, margin: "0 0 10px" }}>How To Order</h1>
        <p style={{ color: "rgba(255,255,255,0.6)", fontFamily: fonts.body, fontSize: 16, maxWidth: 460, margin: "0 auto" }}>
          Ordering from us is simple and fast — just 5 easy steps
        </p>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "60px 24px" }}>
        {steps.map((step, i) => (
          <div key={i} style={{ display: "flex", gap: 24, marginBottom: 36, alignItems: "flex-start", animation: `fadeUp 0.5s ${i * 100}ms both` }}>
            <div style={{ flexShrink: 0, width: 60, height: 60, background: i % 2 === 0 ? accent : B.headerBg, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, boxShadow: `0 4px 16px ${accent}33` }}>
              {step.icon}
            </div>
            <div style={{ flex: 1, paddingTop: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <span style={{ fontFamily: fonts.body, fontSize: 10, fontWeight: 700, color: accent, letterSpacing: "0.1em" }}>STEP {i + 1}</span>
                <div style={{ flex: 1, height: 1, background: B.borderLight }} />
              </div>
              <h3 style={{ fontFamily: fonts.display, fontSize: 18, color: B.text, marginBottom: 8 }}>{step.title}</h3>
              <p style={{ fontFamily: fonts.body, fontSize: 14, color: B.textMid, lineHeight: 1.7 }}>{step.desc}</p>
            </div>
          </div>
        ))}

        {/* CTA */}
        <div style={{ background: B.orangeFaint, border: `1.5px solid ${B.orangeMid}`, borderRadius: 16, padding: 32, textAlign: "center", marginTop: 48 }}>
          <h3 style={{ fontFamily: fonts.display, fontSize: 22, color: B.text, marginBottom: 8 }}>Ready to Order?</h3>
          <p style={{ fontFamily: fonts.body, color: B.textMid, fontSize: 14, marginBottom: 20 }}>Browse our catalog and click WhatsApp on any design to get started.</p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <Btn onClick={() => navigate("/")} variant="primary">Browse Catalog</Btn>
            {branding.whatsapp && (
              <a href={`https://wa.me/${branding.whatsapp.replace(/\D/g,"")}?text=Hi! I want to place an order.`}
                target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#25D366", color: "#fff", padding: "11px 22px", borderRadius: 8, fontWeight: 600, fontSize: 13, textDecoration: "none", fontFamily: fonts.body }}>
                💬 WhatsApp Us
              </a>
            )}
          </div>
        </div>
      </div>

      <SiteFooter branding={branding} categories={categories} />
    </>
  );
}
